import React from 'react';
import { Link } from 'react-router-dom';
import { Button, EmptyState, EmptyStateActions, EmptyStateBody, EmptyStateFooter, Label } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import type { OrganizationDomain } from '../../../types/models/OrganizationDomain';
import type {
  OrganizationDomainTeardownResult,
  OrganizationDomainTeardownStep,
  TeardownOutcome,
} from '../../../types/models/OrganizationDomainResources';
import { DOMAIN_RESOURCE_TYPE_BY_KEY } from './domainResourceTypes';
import type { DomainResourceTypeKey } from './domainResourceTypes';

/** How each outcome reads in the table. Colour supports the text; the text carries the meaning. */
const OUTCOME_LABEL: Record<TeardownOutcome, { text: string; color: 'green' | 'blue' | 'red' | 'grey' }> = {
  deleted: { text: 'Deleted', color: 'green' },
  revoked: { text: 'Revoked', color: 'green' },
  already_absent: { text: 'Already gone', color: 'blue' },
  failed: { text: 'Failed', color: 'red' },
  not_executed: { text: 'Not attempted', color: 'grey' },
};

/** Outcomes that mean the element is gone — `already_absent` included: the end state is the same. */
const SETTLED: readonly TeardownOutcome[] = ['deleted', 'revoked', 'already_absent'];

const typeLabel = (type: OrganizationDomainTeardownStep['type']): string =>
  type === 'organization_domain'
    ? 'Organization domain'
    : DOMAIN_RESOURCE_TYPE_BY_KEY[type as DomainResourceTypeKey].label;

export interface DeleteResourcesResultProps {
  domain: OrganizationDomain;
  result: OrganizationDomainTeardownResult;
  /** Re-submits the same selection; only offered after a partial run. */
  onRetry: () => void;
  /** Re-reads the inventory and returns to step 1 with the selection reconciled against it. */
  onStartOver: () => void;
  submitting: boolean;
}

/**
 * What one teardown run actually did, per element.
 *
 * The run is one synchronous request, so there is no live progress to show — this is a finished
 * result: a status headline, then every step with its own outcome, the skipped ones included. A
 * `partial` result is a warning rather than an error: the steps before the failure really happened,
 * and re-sending the same selection is safe because every step is ensure-absent (plan §5.4).
 *
 * The organization domain being deleted changes where the user can go back to — its detail page no
 * longer exists.
 */
const DeleteResourcesResult: React.FunctionComponent<DeleteResourcesResultProps> = ({
  domain,
  result,
  onRetry,
  onStartOver,
  submitting,
}) => {
  const rows: readonly OrganizationDomainTeardownStep[] = [...result.steps, ...result.not_executed];
  const settled = rows.filter((step) => SETTLED.includes(step.outcome));
  const failed = rows.filter((step) => step.outcome === 'failed');
  const orgDomainGone = result.organization_domain_deleted;

  const status = settled.length === 0 ? 'danger' : result.result === 'partial' ? 'warning' : 'success';
  const titleText =
    settled.length === 0
      ? 'Nothing could be deleted'
      : result.result === 'partial'
        ? `Deleted ${settled.length} of ${rows.length} resources`
        : `Deleted ${settled.length} ${settled.length === 1 ? 'resource' : 'resources'}`;

  const body =
    settled.length === 0
      ? `Nothing was removed for ${domain.name}. The reason is on the failed step below.`
      : result.result === 'partial'
        ? `The run stopped at the first failure, so the steps after it were not attempted. Everything already done stays done — running the same selection again is safe.`
        : orgDomainGone
          ? `Ancalagon no longer hosts anything for ${domain.name}, and the organization domain itself is gone.`
          : `The selected resources are gone. Everything you kept for ${domain.name} is untouched.`;

  return (
    <div>
      <EmptyState titleText={titleText} status={status} variant="lg" headingLevel="h2">
        <EmptyStateBody>{body}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            {result.result === 'partial' && (
              <Button variant="primary" onClick={onRetry} isLoading={submitting} isDisabled={submitting}>
                Retry the rest
              </Button>
            )}
            {orgDomainGone ? (
              <Button
                variant={result.result === 'partial' ? 'link' : 'primary'}
                component={(props) => <Link {...props} to="/organization/domains" />}
              >
                Back to domains
              </Button>
            ) : (
              <>
                <Button
                  variant={result.result === 'partial' ? 'secondary' : 'primary'}
                  component={(props) => <Link {...props} to={`/organization/domains/${domain.id}`} />}
                >
                  Back to domain
                </Button>
                <Button variant="link" onClick={onStartOver} isDisabled={submitting}>
                  Delete more resources
                </Button>
              </>
            )}
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>

      <Table aria-label={`Deletion results for ${domain.name}`} variant="compact" className="pf-v6-u-mt-md">
        <Thead>
          <Tr>
            <Th>Resource</Th>
            <Th>Item</Th>
            <Th>Outcome</Th>
            <Th>Reason</Th>
          </Tr>
        </Thead>
        <Tbody>
          {/* Keyed by position: a step's `element.id` is null when its family was already gone, so two
              steps of the same type and kind can share it. */}
          {rows.map((step, index) => (
            <Tr key={`${index}:${step.type}:${step.element.kind}`}>
              <Td dataLabel="Resource">{typeLabel(step.type)}</Td>
              <Td dataLabel="Item">{step.element.label}</Td>
              <Td dataLabel="Outcome">
                <Label isCompact color={OUTCOME_LABEL[step.outcome].color}>
                  {OUTCOME_LABEL[step.outcome].text}
                </Label>
              </Td>
              <Td dataLabel="Reason">{step.reason ?? '—'}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {failed.length > 0 && (
        <p className="pf-v6-u-mt-md pf-v6-u-text-color-subtle pf-v6-u-font-size-sm">
          A failure here does not undo the steps before it. If retrying does not help, the resource has to be dealt with
          on its own page.
        </p>
      )}
    </div>
  );
};

export default DeleteResourcesResult;
